/*
 * this abomination is a combination of THREE mixins - none of which are really
 *   mixins in the first place, but instead are "don't put all this code in the
 *   component I poorly designed so it won't look like a 1500 line component even
 *   though it really is"
 *
 *   CoverageDatesHelper, CoverageHelper, ClassPlanFromSubGroupCkData
 *
 *   so here we are.  Good luck, and godspeed.
 *
 *   note:  under NO circumstances should any of this garbage move forward into
 *          the Vue 3 update.  This entire area of the site (Member Management)
 *          needs to be rewritten to be - not what this is.
 */
// note:  I am leaving the goofball 'computed' prefix on vars, because I
// don't currently have the time to change all the instances where they
// are in use. ::((

import { computed, getCurrentInstance, ref } from '@vue/composition-api';
import { now, formatDate } from '@utils';
import GroupApi from '@api/GroupApi';

const coverageTemplate = {
  fileBoundId: '',
  effectiveDate: '',
  termDate: '',
  subGroupId: '',
  classId: '',
  planId: '',
  previousEffectiveDate: '',
  previousTerminationDate: '',
  previousSubGroupCk: '',
  previousClassId: '',
  previousPlanId: '',
  benefitOption: '',
  divisionCode: '',
  companyCode: '',
  locationCode: '',
  clientGroupNumber: '',
  reportingCode1: '',
  reportingCode2: '',
  reportingCode3: '',
  reportingCode4: '',
  reportingCode5: ''
};

export const useClassPlans = props => {
  const vm = getCurrentInstance();
  const store = vm.proxy.$store;
  const route = vm.proxy.$route;

  // const subGroupCk = '';  don't need, I don't think
  const coverageHistory = ref(props.coverageHistory || {});
  const loadingCoverages = ref(false);
  const initialCoverageTemplate = ref({ ...coverageTemplate });
  const initialCoverage = ref({});
  const coverage = ref({ ...coverageTemplate });
  const isSubmitting = ref(false);
  // why is this here?  should be on page
  const cancelRoute = {
    name: 'member-details',
    params: {
      memberCk: store.getters['hiddenRouteData/getHiddenRouteData'].memberCk
    }
  };
  const isDependent = ref(false);
  const internalMember = props.member || null; // null will indicated we should always load internalMember from the network, not prop
  const internalCoverageHistory = coverageHistory.value || null;
  const internalPrimarySubscriberCoverages = ref(null);
  const internalSubGroupsInGroup = ref(null);
  const majorDataChangeSatisfied = ref(true);

  const subGroupsInGroup = computed(() => {
    if (internalSubGroupsInGroup.value !== null) {
      return internalSubGroupsInGroup.value;
    }

    if (!computedMember.value || !computedMember.value.groupId) {
      return [];
    }

    const groupId = computedMember.value.groupId;
    new GroupApi({ simulateExample: 'Group Search - Group Id Search' })
      .search({
        id: groupId,
        name: '',
        isParentGroupSearch: false
      })
      .then(res => {
        if (!res.groups) {
          throw new Error(`No groups found for groupId: ${groupId}`);
        }

        const group = res.groups.find(g => g.groupId == groupId);
        if (group) {
          internalSubGroupsInGroup.value = group.subGroups.map(sg => ({
            ...sg,
            groupTerminationDate:
              sg.groupTerminationDate || group.terminationDate,
            groupEffectiveDate: sg.groupEffectiveDate || group.effectiveDate
          }));
        } else {
          console.warn(`Unable to find group ${groupId} in payload`);
        }
      })
      .catch(err => {
        errToString(err).then(errStr => {
          console.error(errStr);
        });
      });

    return internalSubGroupsInGroup.value || [];
  });
  const primarySubscriberCoverages = computed(() => {
    if (
      !computedMember.value ||
      computedMember.value.memberType !== 'Dependent'
    ) {
      return [];
    }

    if (internalPrimarySubscriberCoverages.value !== null) {
      return internalPrimarySubscriberCoverages.value;
    }

    // the subscriberCk can somehow be different than the memberCk of the subscirber. Until the API is
    // updated, the only way to get this is in the member.relatedMembers[] where memberType == "Subscriber"
    const primarySubscriber = computedMember.value.relatedMembers.find(
      m => m.memberType === 'SUBSCRIBER'
    );
    new MemberApi({ simulateExample: 'Multiple Coverage History' })
      .coverageHistory({
        params: {
          memberCk: primarySubscriber.memberCk,
          dateOfService: now()
        }
      })
      .then(res => {
        const primarySubscriberCoverages = res[primarySubscriber.memberCk];
        internalPrimarySubscriberCoverages.value = primarySubscriberCoverages.sort(
          (a, b) => {
            if (moment(a.startDate).isBefore(b.startDate)) {
              return -1;
            }

            return 1;
          }
        );
      });

    return internalPrimarySubscriberCoverages.value || [];
  });
  const primarySubscriberCoverageFromEffectiveDate = computed(() => {
    // tries to find a coverage on the primary subscriber (for dependents only), where the effective date
    // on the form resides between the effective date and term/end date of one of the coverages for the
    // primary subsciber
    return primarySubscriberCoverages.value.find(primaryCoverage => {
      if (
        moment(coverage.value.effectiveDate).isSameOrAfter(
          primaryCoverage.startDate
        )
      ) {
        // if there isn't a term date, eg: on the most recent/last coverage, use that coverage
        if (!primaryCoverage.endDate) {
          return true;
        }

        return moment(coverage.value.effectiveDate).isSameOrBefore(
          primaryCoverage.endDate
        );
      }
    });
  });
  const computedReportingCodes = computed(() => {
    return {
      benefitOption: computedMember.value.benefitOption || '',
      locationCode: computedMember.value.location || '',
      divisionCode: computedMember.value.divisionCode || '',
      companyCode: computedMember.value.companyCode || '',
      clientGroupNumber: computedMember.value.clientGroupNbr || '',
      reportingCode1: computedMember.value.reportingCodes
        ? computedMember.value.reportingCodes[0]
        : '',
      reportingCode2: computedMember.value.reportingCodes
        ? computedMember.value.reportingCodes[1]
        : '',
      reportingCode3: computedMember.value.reportingCodes
        ? computedMember.value.reportingCodes[2]
        : '',
      reportingCode4: computedMember.value.reportingCodes
        ? computedMember.value.reportingCodes[3]
        : '',
      reportingCode5: computedMember.value.reportingCodes
        ? computedMember.value.reportingCodes[4]
        : ''
    };
  });
  const computedIsFirstCoverage = computed(() => {
    if (route.name === 'coverage-add') {
      return false;
    }

    if (computedCoverages.value.length <= 1) {
      return true;
    }

    return isFirstCoverage(coverage.value);
  });
  const computedPlanOptions = computed(() => {
    return plansInClassOptions(coverage.value.classId);
  });
  const classPlanBreakdownSubGroupCk = computed(() => {
    return coverage.value.subGroupCK || 0;
  });
  const computedMember = computed(() => {
    return internalMember.value || {};
  });
  const computedCoverageHistory = computed(() => {
    return internalCoverageHistory.value || {};
  });
  const computedCoveragesInOrderReturnedByService = computed(() => {
    if (
      !computedMember.value ||
      Object.keys(computedCoverageHistory.value).length === 0
    ) {
      return [];
    }

    let coverageHistoryForMember =
      computedCoverageHistory[computedMember.value.memberCk];

    if (!coverageHistoryForMember) {
      return [];
    }

    let orderInService = 0;
    return coverageHistoryForMember.map(c => ({
      displayPlanDescription: `${c.planDescription} - ${c.planId}`,
      displayClassDescription: `${c.classDescription} - ${c.classId}`,
      displaySubGroupName: `${c.subGroupName} - ${c.subGroupId}`,
      subGroup: c.subGroupName,
      subGroupCk: c.subGroupCk,
      subGroupId: c.subGroupId,
      groupCk: c.groupCk,
      classDescription: c.classDescription,
      classId: c.classId,
      planDescription: c.planDescription,
      planId: c.planId,
      effectiveDate: c.startDate,
      termDate: c.endDate,
      productId: c.productId,
      orderInService: orderInService++
    }));
  });
  const computedCoverages = computed(() => {
    return computedCoveragesInOrderReturnedByService.value.sort((a, b) => {
      if (moment(a.effectiveDate).isBefore(b.effectiveDate)) {
        return -1;
      }

      return 1;
    });
  });
  const computedCoverageHeaders = computed(() => {
    return [
      { text: 'Actions', value: 'action' },
      { text: 'Subgroup', value: 'displaySubGroupName' },
      { text: 'Class', align: 'left', value: 'displayClassDescription' },
      { text: 'Plan', value: 'displayPlanDescription' },
      { text: 'Effective Date', value: item => formatDate(item.effectiveDate) },
      { text: 'Term Date', value: item => formatDate(item.termDate) },
      {
        text: 'Order in Service',
        value: 'orderInService',
        sort: true,
        visible: false
      }
    ];
  });
  const computedFirstCoverage = computed(() => {
    if (computedCoverages.value.length === 0) {
      return {};
    }

    return computedCoverages[0];
  });
  const computedLastCoverage = computed(() => {
    if (computedCoverages.value.length === 0) {
      return {};
    }

    return computedCoverages.value[computedCoverages.value.length - 1];
  });
  const computedCoveragesSimilarToCurrent = computed(() => {
    return coveragesWithSameSubGroupPlanAndClass(coverage.value);
  });
  const computedOtherCoveragesSimilarToCurrent = computed(() => {
    return coveragesWithSameSubGroupPlanAndClass(coverage.value).filter(
      c => !compareCoverages(c, coverage.value)
    );
  });
  const computedAdjacentCoverage = computed(() => {
    let siblings = {
      prior: null,
      next: null
    };

    const coverageSetForComparison = computedCoveragesSimilarToCurrent.value;
    if (coverageSetForComparison.length === 0) {
      return siblings;
    }

    let whereCoverageIndexWouldBe = -1;
    for (let i = 0; i < coverageSetForComparison.length; ++i) {
      let similarCoverage = coverageSetForComparison[i];
      if (
        moment(coverage.value.effectiveDate).isAfter(
          similarCoverage.effectiveDate
        )
      ) {
        // the coverage index would be, or slot into, one after the coverage it's after
        whereCoverageIndexWouldBe = i + 1;
      }
    }

    if (whereCoverageIndexWouldBe !== -1) {
      siblings.prior = coverageSetForComparison[whereCoverageIndexWouldBe - 1];
      siblings.next = coverageSetForComparison[whereCoverageIndexWouldBe + 1];
    } else {
      const firstSimilarÇoverage = coverageSetForComparison[0];
      if (!compareCoverages(coverage.value, firstSimilarÇoverage)) {
        siblings.prior = firstSimilarÇoverage;
      }
    }

    return siblings;
  });
  const computedWarnings = computed(() => {
    let warnings = [];

    if (!majorDataChangeSatisfied.value) {
      warnings.push({
        formId: 'effective-date',
        text: 'Must modify coverage to submit',
        type: 'notice'
      });
    }

    if (
      coverage.value.effectiveDate &&
      !moment(coverage.value.effectiveDate).isSameOrAfter(
        computedMember.value.dateOfBirth
      )
    ) {
      warnings.push({
        formId: 'effective-date',
        text:
          'Effective date must be on or after date of birth ' +
          formatDate(computedMember.value.dateOfBirth)
      });
    }

    if (coverage.value.effectiveDate && coverage.value.termDate) {
      if (
        moment(coverage.value.termDate).isSameOrBefore(
          coverage.value.effectiveDate
        )
      ) {
        warnings.push({
          formId: 'term-date',
          text:
            'Term date must be after effective date of ' +
            formatDate(coverage.value.effectiveDate)
        });
      }
    }

    if (isDependent.value) {
      if (
        coverage.value.effectiveDate &&
        !primarySubscriberCoverageFromEffectiveDate.value
      ) {
        warnings.push({
          formId: 'effective-date',
          text:
            "Dependent's effective date must be within the subscriber's coverage period"
        });

        // per 4/11/19 discussion: the only validations for dependent would be:
        // 1) Effective >= DOB
        // 2) w /in subscriber's coverage
        // 3) Effective doesn't overlap a coverage record w/ a term date for that dependent
        // if it fails 2 (which is this if block) don't bother checking any further for overlaps
        return warnings;
      }

      if (
        primarySubscriberCoverageFromEffectiveDate.value &&
        coverage.value.termDate &&
        moment(coverage.value.termDate).isAfter(
          primarySubscriberCoverageFromEffectiveDate.value.endDate
        )
      ) {
        warnings.push({
          formId: 'term-date',
          text:
            "Term date must be on or before subscriber's term date " +
            formatDate(primarySubscriberCoverageFromEffectiveDate.value.endDate)
        });
      }
    }

    if (subGroupCk.value && coverage.value.classId && coverage.value.planId) {
      const subGroupAccess = isInternalUser.value
        ? computedFilteredSubGroupsInGroup.value
        : subGroupAccess.value;
      if (subGroupAccess && subGroupAccess.length > 0) {
        const subGroup = subGroupAccess.find(
          s => s.subGroupCk == subGroupCk.value
        );

        if (subGroup) {
          const subGroupEffectiveDate =
            subGroup.subGroupEffectiveDate || subGroup.effectiveDate;
          const subGroupTerminationDate =
            subGroup.subGroupTerminationDate || subGroup.terminationDate;

          if (coverage.value.effectiveDate) {
            if (
              subGroupEffectiveDate &&
              !moment(coverage.value.effectiveDate).isSameOrAfter(
                subGroupEffectiveDate
              )
            ) {
              warnings.push({
                formId: 'effective-date',
                text:
                  'Effective date must be on or after subgroup effective date of ' +
                  formatDate(subGroupEffectiveDate)
              });
            }

            if (
              subGroup.groupEffectiveDate &&
              !moment(coverage.value.effectiveDate).isSameOrAfter(
                subGroup.groupEffectiveDate
              )
            ) {
              warnings.push({
                formId: 'effective-date',
                text:
                  'Effective date must be on or after group effective date of ' +
                  formatDate(subGroup.groupEffectiveDate)
              });
            }

            if (
              subGroupTerminationDate &&
              !moment(coverage.value.effectiveDate).isSameOrBefore(
                subGroupTerminationDate
              )
            ) {
              warnings.push({
                formId: 'effective-date',
                text:
                  'Effective date must be on or before subgroup term date of ' +
                  formatDate(subGroupTerminationDate)
              });
            }

            if (
              subGroup.groupTerminationDate &&
              !moment(coverage.value.effectiveDate).isSameOrBefore(
                subGroup.groupTerminationDate
              )
            ) {
              warnings.push({
                formId: 'effective-date',
                text:
                  'Effective date must be on or before group term date of ' +
                  formatDate(subGroup.groupTerminationDate)
              });
            }
          }

          if (coverage.value.termDate) {
            if (
              subGroupTerminationDate &&
              !moment(coverage.value.termDate).isSameOrBefore(
                subGroupTerminationDate
              )
            ) {
              warnings.push({
                formId: 'term-date',
                text:
                  'Term date must be on or before subgroup termination date of ' +
                  formatDate(subGroupTerminationDate)
              });
            }

            if (
              subGroup.groupTerminationDate &&
              !moment(coverage.value.termDate).isSameOrBefore(
                subGroup.groupTerminationDate
              )
            ) {
              warnings.push({
                formId: 'term-date',
                text:
                  'Term date must be on or before group termination date of ' +
                  formatDate(subGroup.groupTerminationDate)
              });
            }
          }
        }
      }

      const klass = getFirstClassByClassId(coverage.value.classId);
      if (klass) {
        if (
          coverage.value.effectiveDate &&
          !moment(coverage.value.effectiveDate).isSameOrAfter(
            klass.classEffectiveDate
          )
        ) {
          warnings.push({
            formId: 'effective-date',
            text:
              'Effective date must be on or after class effective date of ' +
              formatDate(klass.classEffectiveDate)
          });
        }

        if (
          coverage.value.termDate &&
          klass.classTerminationDate &&
          !moment(coverage.value.termDate).isSameOrBefore(
            klass.classTerminationDate
          )
        ) {
          warnings.push({
            formId: 'term-date',
            text:
              'Term date must be on or before class term date of ' +
              formatDate(klass.classTerminationDate)
          });
        }

        if (
          coverage.value.effectiveDate &&
          klass.classTerminationDate &&
          !moment(coverage.value.effectiveDate).isSameOrBefore(
            klass.classTerminationDate
          )
        ) {
          warnings.push({
            formId: 'effective-date',
            text:
              'Effective date must be on or before class termination date of ' +
              formatDate(klass.classTerminationDate)
          });
        }
      }

      const plan = getFirstPlanByClassAndPlanId(
        coverage.value.classId,
        coverage.value.planId
      );
      if (plan && Object.keys(plan).length !== 0) {
        if (
          coverage.value.termDate &&
          plan.planTerminationDate &&
          !moment(coverage.value.termDate).isSameOrBefore(
            plan.planTerminationDate
          )
        ) {
          warnings.push({
            formId: 'term-date',
            text:
              'Term date must be on or before plan term date of ' +
              formatDate(plan.planTerminationDate)
          });
        }

        if (
          coverage.value.effectiveDate &&
          plan.planEffectiveDate &&
          !moment(coverage.value.effectiveDate).isSameOrAfter(
            plan.planEffectiveDate
          )
        ) {
          warnings.push({
            formId: 'effective-date',
            text:
              'Effective date must be on or after plan effective date of ' +
              formatDate(plan.planEffectiveDate)
          });
        }

        if (
          coverage.value.effectiveDate &&
          plan.planTerminationDate &&
          !moment(coverage.value.effectiveDate).isSameOrBefore(
            plan.planTerminationDate
          )
        ) {
          warnings.push({
            formId: 'effective-date',
            text:
              'Effective date must be on or before plan termination date of ' +
              formatDate(plan.planTerminationDate)
          });
        }
      }
    }

    if (computedCoverages.value.length > 0) {
      if (!computedIsFirstCoverage.value) {
        if (
          coverage.value.effectiveDate &&
          !moment(coverage.value.effectiveDate).isSameOrAfter(
            computedFirstCoverage.value.effectiveDate
          )
        ) {
          warnings.push({
            formId: 'effective-date',
            text:
              'Effective date must be on or after original effective date of ' +
              formatDate(computedFirstCoverage.value.effectiveDate)
          });
        }
      }

      for (
        let i = 0;
        i < computedOtherCoveragesSimilarToCurrent.value.length;
        i++
      ) {
        let similarCoverage = computedOtherCoveragesSimilarToCurrent[i].value;

        // skip comparing coverage to self initial value on edit
        if (compareCoverages(initialCoverage.value, similarCoverage)) {
          continue;
        }

        const genericCoverageError = 'Coverages cannot overlap';
        const MOMENT_INCLUSIVE_FLAG = '[]'; // [] in moment indicated inclusive dates

        // CONDITIONS FOR FAILURE
        // C#1       ┌----┐             Am I in my sibling
        // C#1       ┌┐
        // C#2               ┌------▷   If sibling does not have a term date, my effective date cannot be after that effective date
        //
        // |Jan|Feb|Mar|Apr|May|
        // |YYY|   |YYY|   |YYYYYYYYY   Y = ranges they have coverage. There a three sibling coverages in this scenario (Jan, Mar, May)

        // Condition #1: Do I overlap any of my sibling coverages
        if (
          coverage.value.effectiveDate &&
          moment(coverage.value.effectiveDate).isBetween(
            similarCoverage.effectiveDate,
            similarCoverage.termDate,
            null,
            MOMENT_INCLUSIVE_FLAG
          )
        ) {
          console.warn('Condition #1 triggered', similarCoverage);
          warnings.push({
            formId: 'effective-date',
            text: genericCoverageError
          });
          break;
        }

        // Condition #2: If sibling does not have a term date, my effective date cannot be after that effective date
        if (
          (!similarCoverage.termDate ||
            similarCoverage.termDate.trim() === '' ||
            !moment(similarCoverage.termDate).isValid()) &&
          moment(coverage.value.effectiveDate).isSameOrAfter(
            similarCoverage.effectiveDate
          )
        ) {
          console.warn('Condition #0 triggered', similarCoverage);
          warnings.push({
            formId: 'effective-date',
            text: genericCoverageError
          });
          break;
        }
      }
    }

    return warnings;
  });
  // TODO:  this should be done on the page itself, not here.
  const computedSubmitEnabled = computed(() => {
    return computedWarnings.value.length === 0 && this.formReady;
  });
  const computedFilteredSubGroupsInGroup = computed(() => {
    if (subGroupFilter.value && typeof subGroupFilter.value === 'function') {
      return subGroupsInGroup.value.filter(subGroupFilter.value);
    }

    return subGroupsInGroup.value;
  });
  const computedFilteredSubGroupsInGroupOptions = computed(() => {
    if (computedFilteredSubGroupsInGroup.value.length === 0) {
      return [];
    }

    return computedFilteredSubGroupsInGroup.value.map(sg => ({
      value: sg.subGroupCk,
      text: `${sg.subGroupName} - ${sg.subGroupId}`
    }));
  });
  const computedRelatedMembers = computed(() => {
    if (!computedMember.value || !computedMember.value.relatedMembers) {
      return [];
    }

    return computedMember.value.relatedMembers.filter(
      m => m.memberCk != computedMember.value.memberCk
    );
  });
  const computedRelatedNonSubscribers = computed(() => {
    return computedRelatedMembers.value.filter(
      m => m.memberType !== 'SUBSCRIBER'
    );
  });
  const relatedMembers = computed(() => {
    if (!computedMember.value || !computedMember.value.relatedMembers) {
      return [];
    }

    return computedMember.value.relatedMembers.map(relation => {
      return {
        memberCk: relation.memberCk,
        memberType: relation.memberType,
        actionType: 'AddCoverage',
        memberVoided: relation.memberVoided
      };
    });
  });

  const isFirstCoverage = coverage => {
    return compareCoverages(computedFirstCoverage.value, coverage);
  };
  const coverageInRangeIfDependent = callback => {
    // if your currently addinng/editing a dependent, they're sg, class, and plan
    // all get locked/disabled and automatically get populated with the sg, class, and plan
    // of the primary subscibers coverage that has an effective date before the effective date
    // selected on the from
    if (!isDependent.value) {
      return true;
    }

    if (!primarySubsciberCoverageFromEffectiveDate.value) {
      return false;
    }

    return callback(primarySubsciberCoverageFromEffectiveDate.value);
  };
  const subGroupFilter = subGroup => {
    const coverageInRangeIfDependent = coverageInRangeIfDependent(
      coverage => coverage.subGroupCk == subGroup.subGroupCk
    );
    if (isInternalUser.value) {
      return coverageInRangeIfDependent;
    }

    // only show subGroups where the member belongs to that group
    return (
      `${subGroup.groupCk}` === `${computedMember.value.groupCk}` &&
      subGroup.access.memberMaintenanceAllowed &&
      coverageInRangeIfDependent(
        coverage => coverage.subGroupCk == subGroup.subGroupCk
      )
    );
  };
  const classFilter = klass => {
    return (
      coverage.value.subGroupCk == klass.subGroupCk &&
      coverageInRangeIfDependent(coverage => coverage.classId == klass.classId)
    );
  };
  const planFilter = plan => {
    return (
      coverage.value.classId == plan.classId &&
      coverageInRangeIfDependent(coverage => coverage.planId == plan.planId)
    );
  };
  const coveragesWithSameSubGroupPlanAndClass = coverage => {
    return computedCoverages.value.filter(c => {
      return (
        c.subGroupCk == coverage.subGroupCk &&
        c.classId == coverage.classId &&
        c.planId == coverage.planId
      );
    });
  };
  const compareCoverages = (coverage1, coverage2) => {
    const compare =
      Boolean(`${coverage1.subGroupCk}` === `${coverage2.subGroupCk}`) &&
      Boolean(coverage1.classId === coverage2.classId) &&
      Boolean(coverage1.planId === coverage2.planId) &&
      Boolean(
        formatDate(coverage1.effectiveDate) ===
          formatDate(coverage2.effectiveDate)
      ) &&
      Boolean(
        formatDate(coverage1.termDate) === formatDate(coverage2.termDate)
      );

    return compare;
  };
  const loadCoverages = memberCk => {
    loadingCoverages.value = true;

    new Promise((resolve, reject) => {
      if (internalMember.value != null) {
        // is only !null if member was passed as prop, so we won't every try to request from network
        resolve(internalMember.value);
      } else {
        new MemberApi({ simulateExample: 'Member Details - Termed Member' })
          .memberDetails({
            params: {
              memberCk: memberCk,
              isEligibility: false,
              isBenefit: false,
              dateOfService: now()
            }
          })
          .then(res => {
            resolve(res.member);
          });
      }
    })
      .then(member => {
        internalMember.value = { ...member };

        isDependent.value = internalMember.value.memberType === 'Dependent';

        initialCoverage.value = {
          ...coverageTemplate,
          ...computedReportingCodes.value
        };

        return member;
      })
      .then(res => {
        if (internalCoverageHistory.value === null) {
          new MemberApi({ simulateExample: 'Multiple Coverage History' })
            .coverageHistory({
              params: {
                memberCk: memberCk,
                dateOfService: now()
              }
            })
            .then(res => {
              internalCoverageHistory.value = { ...res };
            })
            .catch(res => {
              // TODO: handle error
            });
        }

        return res;
      })
      .catch(err => {
        errToString(err).then(errStr => {
          console.error(errStr);
        });
      })
      .finally(() => {
        loadingCoverages.value = false;
      });
  };

  ///

  const classEffectiveDate = computed(() => {
    if (currentClass.value) {
      return currentClass.value.classEffectiveDate;
    }

    return null;
  });
  const classPlanSubGroups = ref([]);
  const classTermDate = computed(() => {
    if (currentClass.value) {
      return currentClass.value.classTerminationDate;
    }

    return null;
  });
  const computedClasses = computed(() => {
    const classes = {};
    computedClassPlans.value.forEach(cp => {
      if (classes.hasOwnProperty(cp.classId)) {
        return;
      }

      classes[cp.classId] = {
        classId: cp.classId,
        className: cp.classDescription,
        subGroupId: cp.subGroupId,
        subGroupCk: cp.subGroupCk
      };
    });
    // console.log(`classes`, classes, Object.values(classes));
    return Object.values(classes);
  });
  const computedClassPlanDictionary = computed(() => {
    const classPlans = {};
    computedClassPlans.value.forEach(c => {
      if (!classPlans[c.classId]) {
        classPlans[c.classId] = {
          classDescription: c.classDescription,
          classEffectiveDate: c.classEffectiveDate,
          classTerminationDate: c.classTerminationDate,
          classId: c.classId,
          plans: {}
        };
      }

      if (!classPlans[c.classId].plans[c.planId]) {
        classPlans[c.classId].plans[c.planId] = {
          planId: c.planId,
          planDescription: c.planDescription,
          planEffectiveDate: c.planEffectiveDate,
          planTerminationDate: c.planTerminationDate,
          subGroupInfo: c.subGroupInfo
        };
      }
    });

    return classPlans;
  });
  const computedClassPlans = computed(() => {
    let classPlans = [];

    classPlanSubGroups.value.forEach(sg => {
      if (!sg.classPlan) {
        return;
      }

      sg.classPlan.forEach(cp => {
        classPlans.push({
          ...cp,
          ...{
            subGroupCk: sg.subGroupCk,
            subGroupId: sg.subGroupId,
            subGroupInfo: sg,
            groupTerminationDate: sg.groupTerminationDate,
            groupEffectiveDate: sg.groupEffectiveDate
          }
        });
      });
    });

    return classPlans;
  });
  const computedCurrentSubGroupPlanInfo = computed(() => {
    if (!computedClassPlans.value || computedClassPlans.value.length === 0) {
      return {};
    }

    let plan = computedClassPlans.value.find(
      sg => `${sg.subGroupCk}` === `${inContext.value.subGroupCk}`
    );
    if (plan) {
      return plan.subGroupInfo;
    }

    return {};
  });
  const computedFilteredClasses = computed(() => {
    if (classFilter.value && typeof classFilter.value === 'function') {
      return computedClasses.value.filter(classFilter.value);
    }

    return computedClasses.value;
  });
  const computedFilteredClassOptions = computed(() => {
    return computedFilteredClasses.value.map(c => ({
      value: c.classId,
      text: `${c.className} - ${c.classId}`
    }));
  });
  const computedFilteredPlanOptions = computed(() => {
    return computedFilteredPlans.value.map(p => ({
      value: p.planId,
      text: `${p.planName} - ${p.planId}`
    }));
  });
  const computedFilteredPlans = computed(() => {
    // console.log(`computedFilteredPlans`, planFilter.value, computedPlans.value);
    if (planFilter.value && typeof planFilter.value === 'function') {
      return computedPlans.value.filter(planFilter.value);
    }

    return computedPlans.value;
  });
  const computedFilteredSubGroups = computed(() => {
    const subGroupAccess = subGroupAccess.value || [];
    if (subGroupFilter.value && typeof subGroupFilter.value === 'function') {
      return subGroupAccess.filter(subGroupFilter.value);
    }

    return subGroupAccess;
  });
  const computedFilteredSubGroupOptions = computed(() => {
    if (computedFilteredSubGroups.value.length === 0) {
      return [];
    }

    let subGroupsInGroup = computedFilteredSubGroups.value.sort((g1, g2) =>
      g1.subGroupName.localeCompare(g2.subGroupName)
    );
    subGroupsInGroup = subGroupsInGroup.map(sg => ({
      value: sg.subGroupCk,
      text: `${sg.subGroupName} - ${sg.subGroupId}`
    }));

    return subGroupsInGroup;
  });
  const computedMemberIdSource = computed(() => {
    try {
      return classPlanSubGroups.value[0].memberIdSource;
    } catch (error) {
      return '';
    }
  });
  const computedPlans = computed(() => {
    const plans = [];
    computedClassPlans.value.forEach(cp => {
      // console.log(`plans: ${cp.plansId}`, plans.hasOwnProperty(cp.planId));
      if (plans.hasOwnProperty(cp.planId)) {
        return;
      }

      plans.push({
        classId: cp.classId,
        planId: cp.planId,
        planName: cp.planDescription,
        subGroupId: cp.subGroupId,
        subGroupCk: cp.subGroupCk,
        subGroupInfo: cp.subGroupInfo
      });
    });
    // console.log(`plans`, plans);
    return plans;
  });
  const currentClass = computed(() => {
    return getFirstClassByClassId(classId.value);
  });
  const currentPlan = computed(() => {
    return getFirstPlanByClassAndPlanId(classId.value, planId.value);
  });
  const getClassPlansForSubGroup = subGroupCk => {
    return classPlanSubGroups.value.find(sg => sg.subGroupCK === subGroupCk);
  };
  const getFirstClassByClassId = classId => {
    return computedClassPlanDictionary.value[classId];
  };
  const getFirstPlanByClassAndPlanId = (classId, planId) => {
    const classFound = computedClassPlanDictionary.value[classId];
    if (classFound && classFound.plans) {
      return classFound.plans[planId];
    }
    return {};
  };
  const groupEffectiveDate = computed(() => {
    if (computedCurrentSubGroupPlanInfo.value.groupEffectiveDate) {
      return computedCurrentSubGroupPlanInfo.value.groupEffectiveDate;
    }

    return null;
  });
  const groupTermDate = computed(() => {
    if (computedCurrentSubGroupPlanInfo.value.groupTerminationDate) {
      return computedCurrentSubGroupPlanInfo.value.groupTerminationDate;
    }
    return null;
  });
  const inContext = computed(() => store.getters['groupSelector/selected']);
  const planEffectiveDate = computed(() => {
    if (currentPlan.value) {
      return currentPlan.value.planEffectiveDate;
    }

    return null;
  });
  const planTermDate = computed(() => {
    if (currentPlan.value) {
      return currentPlan.value.planTerminationDate;
    }

    return null;
  });
  const plansInClassOptions = classId => {
    if (!computedClassPlanDictionary.value[classId]) {
      return [];
    }

    const plans = computedClassPlanDictionary.value[classId].plans;
    return Object.keys(plans).map((key, index) => ({
      value: plans[key].planId,
      text: plans[key].planDescription
    }));
  };
  const requestingNewClassPlans = ref(false);
  // TODO: make this a store action
  const requestClassPlansForSubGroup = (subGroupCk, isAdd = false) => {
    // console.log(`requestClassPlansForSubGroup`, subGroupCk);
    if (!subGroupCk) {
      return;
    }

    requestingNewClassPlans.value = true;
    const payload = { subGroupCk };
    if (isAdd) {
      payload.type = 'ADD';
    }

    new GroupApi({ simulateExample: 'Multiple Class Plans for SubGroup.' })
      .classPlans(payload)
      .then(res => {
        classPlanSubGroups.value = res.subGroups || [];
      })
      .catch(err => {
        console.error(err);
      })
      .finally(() => {
        requestingNewClassPlans.value = false;
      });
  };
  const subGroupEffectiveDate = computed(() => {
    const effectiveDate =
      computedCurrentSubGroupPlanInfo.value.effectiveDate ||
      computedCurrentSubGroupPlanInfo.value.subGroupEffectiveDate;
    if (effectiveDate) {
      return effectiveDate;
    }

    return null;
  });
  const subGroupTermDate = computed(() => {
    const termDate =
      computedCurrentSubGroupPlanInfo.value.termDate ||
      computedCurrentSubGroupPlanInfo.value.terminationDate ||
      computedCurrentSubGroupPlanInfo.value.subGroupTerminationDate;
    if (termDate) {
      return termDate;
    }
    return null;
  });

  return {
    cancelRoute,
    classEffectiveDate,
    classPlanSubGroups,
    classTermDate,
    computedClasses,
    computedClassPlanDictionary,
    computedClassPlans,
    computedCurrentSubGroupPlanInfo,
    computedFilteredClasses,
    computedFilteredClassOptions,
    computedFilteredPlans,
    computedFilteredPlanOptions,
    computedFilteredSubGroups,
    computedFilteredSubGroupOptions,
    computedMemberIdSource,
    computedPlans,
    coverageHistory,
    currentClass,
    currentPlan,
    getClassPlansForSubGroup,
    getFirstClassByClassId,
    getFirstPlanByClassAndPlanId,
    groupEffectiveDate,
    groupTermDate,
    isSubmitting,
    loadCoverages,
    loadingCoverages,
    planEffectiveDate,
    planTermDate,
    plansInClassOptions,
    requestingNewClassPlans,
    requestClassPlansForSubGroup,
    subGroupEffectiveDate,
    subGroupTermDate
  };
};
